import React, { useState, useEffect } from 'react';
import axios from 'axios';
import ReactMarkdown from 'react-markdown';
// Removed useNavigate and AuthContext imports as they are no longer needed here

// Accept user and onTermsSigned callback as props
const TermsAndConditions = ({ user, onTermsSigned }) => {
  const [selectedLang, setSelectedLang] = useState('no'); // Default to Norwegian instead of English
  const [termsContent, setTermsContent] = useState('');
  const [termsVersion, setTermsVersion] = useState(''); // Version is independent of language
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [agreed, setAgreed] = useState(false);
  // Removed useNavigate and useContext

  // Fetch terms content when selectedLang changes
  useEffect(() => {
    const fetchTerms = async () => {
      setIsLoading(true);
      setError('');
      setTermsContent(''); // Clear previous content while loading new language
      try {
        const token = localStorage.getItem('token');
        const config = {
          headers: { 'x-auth-token': token },
          params: { lang: selectedLang } // Pass selected language as query param
        };
        const res = await axios.get('/api/terms', config);
        setTermsContent(res.data.content);
        setTermsVersion(res.data.version); // Version should be consistent across languages
      } catch (err) {
        console.error(`Error fetching terms for lang=${selectedLang}:`, err);
        setError(`Failed to load terms for ${selectedLang.toUpperCase()}. Please try another language or contact support.`);
      } finally {
        setIsLoading(false);
      }
    };

    fetchTerms();
  }, [selectedLang]); // Re-fetch when selectedLang changes

  const handleAgree = async () => {
    if (!agreed) {
      setError('You must check the box to agree to the terms.');
      return;
    }
    setIsSubmitting(true);
    setError('');
    try {
      const token = localStorage.getItem('token');
      const config = {
        headers: {
          'Content-Type': 'application/json',
          'x-auth-token': token,
        },
      };
      await axios.post('/api/auth/me/sign-terms', { version: termsVersion }, config);

      // Call the callback function provided by Dashboard to refresh user data
      // and potentially hide this component
      if (onTermsSigned) {
        await onTermsSigned();
      }

      // No need to navigate here, Dashboard will re-render with Outlet

    } catch (err) {
      console.error('Error signing terms:', err);
      setError(err.response?.data?.message || 'Failed to submit agreement. Please try again.');
      setIsSubmitting(false);
    }
    // No finally block needed here as parent component handles re-render
  };

  // Removed user loading/role checks - parent component (Dashboard) handles rendering this

  return (
    // Added a wrapper div for better layout control within Dashboard
    <div className="flex items-center justify-center min-h-full p-4 bg-gray-100">
      <div className="container mx-auto p-4 sm:p-6 lg:p-8 max-w-4xl bg-white rounded-lg shadow-xl border border-gray-200">
        {/* Language Selection with Flags */}
        <div className="flex justify-center space-x-3 mb-6 border-b pb-4">
          {[
            // English removed from the UI
            { code: 'no', flag: '🇳🇴' },
            { code: 'sv', flag: '🇸🇪' },
            { code: 'de', flag: '🇩🇪' },
          ].map((langInfo) => (
            <button
              key={langInfo.code}
              onClick={() => setSelectedLang(langInfo.code)}
              className={`px-3 py-2 rounded-lg text-2xl transition-all duration-150 ${ // Increased padding and text size for flags
                selectedLang === langInfo.code
                  ? 'bg-blue-100 ring-2 ring-blue-500 scale-110' // Highlight selected flag
                  : 'bg-gray-100 hover:bg-gray-200 opacity-75 hover:opacity-100'
              }`}
              title={langInfo.code.toUpperCase()} // Tooltip for accessibility
            >
              {langInfo.flag}
            </button>
          ))}
        </div>

        <h1 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-6 text-center">
          Tutor Terms and Conditions ({termsVersion}) {/* Removed language from title as flag is selected */}
        </h1>

        {isLoading && (
        <div className="flex justify-center items-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500"></div>
        </div>
      )}

      {error && !isLoading && (
        <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded relative mb-4" role="alert">
          <strong className="font-bold">Error: </strong>
          <span className="block sm:inline">{error}</span>
        </div>
      )}

      {!isLoading && !error && (
        <>
          <div className="prose prose-lg max-w-none bg-white p-6 rounded-lg shadow border border-gray-200 mb-6 max-h-[60vh] overflow-y-auto">
            {/* Render the markdown content */}
            {/* Render the markdown content */}
            <ReactMarkdown>{termsContent}</ReactMarkdown>
          </div>

          <div className="bg-gray-50 p-4 rounded-lg shadow mt-6">
            <div className="flex items-center mb-4">
              <input
                id="agree-checkbox"
                type="checkbox"
                checked={agreed}
                onChange={(e) => setAgreed(e.target.checked)}
                className="h-5 w-5 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
              />
              <label htmlFor="agree-checkbox" className="ml-3 block text-sm font-medium text-gray-700">
                I have read, understood, and agree to the Tutor Terms and Conditions ({termsVersion}).
              </label>
            </div>

            <button
              onClick={handleAgree}
              disabled={!agreed || isSubmitting}
              className={`w-full inline-flex justify-center items-center px-6 py-3 border border-transparent text-base font-medium rounded-md shadow-sm text-white ${
                !agreed || isSubmitting
                  ? 'bg-gray-400 cursor-not-allowed'
                  : 'bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500'
              }`}
            >
              {isSubmitting ? (
                <>
                  <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  Submitting...
                </>
              ) : (
                'Agree and Continue'
              )}
            </button>
          </div>
        </>
      )}
      </div>
    </div>
  );
};

export default TermsAndConditions;
